/**
 * Rider-charge rows shown at the top of SummaryGrid, above the premiums, for
 * coverages whose quotes carry a flat rider charge per covered relationship
 * (Critical Illness). One row per relationship, one cell per plan.
 */

import type { SummaryRowData } from "./summary-rows";
import type { BenefitPlanGroup, RiderCharges } from "../types/models";

export type RiderChargeRelationship = keyof RiderCharges;

/** Marks a row as a rider-charge row; the value is the row's relationship. */
export const RIDER_CHARGE_ROW_KEY = "__riderChargeRelationship";

export const RIDER_CHARGE_ROWS: readonly {
  readonly relationship: RiderChargeRelationship;
  readonly label: string;
}[] = [
  { relationship: "employee", label: "Employee Rider Charge" },
  { relationship: "spouse", label: "Spouse Rider Charge" },
  { relationship: "child", label: "Child(ren) Rider Charge" },
];

/** Rider charges are USD to three decimal places. */
export const RIDER_CHARGE_FRACTION_DIGITS = 3;

/**
 * Edits a plan's rider charges. SummaryGrid shows the rider-charge rows only
 * when one is supplied.
 */
export interface RiderChargeEditor {
  /** Called with only the edited charge; null clears it. */
  readonly onChange: (planId: string, charges: RiderCharges) => void;
  /** False renders the plan's charges read-only. */
  readonly isEditable: (planId: string) => boolean;
}

export const riderChargeRelationshipOf = (
  row: SummaryRowData | undefined,
): RiderChargeRelationship | undefined =>
  RIDER_CHARGE_ROWS.find(
    ({ relationship }) => row?.[RIDER_CHARGE_ROW_KEY] === relationship,
  )?.relationship;

/** The editable rider-charge input of one plan's cell. */
export const riderChargeInputTestId = (
  relationship: RiderChargeRelationship,
  planId: string,
): string => `rider-charge-${relationship}-${planId}`;

export const roundRiderCharge = (value: number): number =>
  Number(value.toFixed(RIDER_CHARGE_FRACTION_DIGITS));

/** A patch holding just one relationship's charge, rounded; null clears it. */
export const riderChargePatch = (
  relationship: RiderChargeRelationship,
  value: number | null,
): RiderCharges => ({
  [relationship]: value === null ? null : roundRiderCharge(value),
});

/** One row per relationship, each keyed by plan id like the premium rows. */
export const buildRiderChargeRows = (
  groups: readonly BenefitPlanGroup[],
): SummaryRowData[] => {
  const plans = groups.flatMap((group) => group.benefitPlans);
  return RIDER_CHARGE_ROWS.map(({ relationship, label }) => ({
    summaryType: label,
    [RIDER_CHARGE_ROW_KEY]: relationship,
    ...Object.fromEntries(
      plans.map((plan) => [plan.id, plan.riderCharges?.[relationship] ?? null]),
    ),
  }));
};
